import React, { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import isEmpty from 'lodash/isEmpty';
import { fetchPlacements } from 'core/actions/ocapi';
import { selectNumFavoriteMerchants, selectFavoritesIsProcessed } from 'core/selectors/favorites';
import List from 'core/modules/Partials/List/List';
import { selectActiveMQ } from 'core/modules/MediaQueries/selectors';
import { useSubscribeMember } from 'core/modules/Forms/hooks/useSubscribeMember';
import orgConfig from 'org/configs/org.config';
import PotentialFavorite from './components/PotentialFavorite/PotentialFavorite';
import { triggerOnboardingFavoritesDoneClicked } from '../../actions/index';
import SlideCounter from '../SlideCounter/SlideCounter';

import {
  selectFavoritesConfig,
  selectFavoritesIsLoading,
  selectFavoritesIsLoaded,
  selectFavoriteOnlineMerchants,
} from './selectors';

function Favorites({ handleFavoritesDoneClick, stepsData }) {
  const dispatch = useDispatch();

  const config = useSelector(selectFavoritesConfig);
  const isLoading = useSelector(selectFavoritesIsLoading);
  const isLoaded = useSelector(selectFavoritesIsLoaded);
  const onlineMerchants = useSelector(selectFavoriteOnlineMerchants);
  const numFavoriteMerchants = useSelector(selectNumFavoriteMerchants);
  const favoritesIsProcessed = useSelector(selectFavoritesIsProcessed);
  const numFavoritesStartedWith = useRef(0);
  const {
    favoritesHeading, optInNotice, checkShouldUseCarousel, favoritesCTAText, getFavoritesText,
    subscribeOnFavoritesOptions, shouldSubscribeMemberOnFavorites, carousel, use120x60Logo,
    shouldShowLaterButton,
  } = config;
  const { earnType } = orgConfig;
  const activeMQ = useSelector(selectActiveMQ);
  const subscribeMemberOnFavoritesAlerts = useSubscribeMember(subscribeOnFavoritesOptions);

  const favoritesText = getFavoritesText(earnType);
  const shouldRenderCarousel = checkShouldUseCarousel(activeMQ);

  useEffect(() => {
    if (favoritesIsProcessed) {
      numFavoritesStartedWith.current = numFavoriteMerchants;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [favoritesIsProcessed]);

  useEffect(() => {
    if (!isLoaded && !isLoading) {
      const { api: { options, params } } = config;
      dispatch(fetchPlacements(params, options));
    }
  });

  const handleFavoritesDoneClickWithAnalyticsAction = (isNextButton) => {
    const numNewFavorites = numFavoriteMerchants - numFavoritesStartedWith.current;
    const action = isNextButton ? 'Favorites done click' : 'Favorites pick later click';

    // If member picked favorites during onboarding opt the memeber in to the Faves stream
    if (isNextButton && numNewFavorites && shouldSubscribeMemberOnFavorites) {
      subscribeMemberOnFavoritesAlerts();
    }

    dispatch(triggerOnboardingFavoritesDoneClicked(action, numNewFavorites));
    handleFavoritesDoneClick();
  };

  const renderPotentialFavorite = (merchant) => {
    const { id, name, logoUrls } = merchant;

    return (
      <PotentialFavorite
        key={id}
        merchantId={id}
        merchantName={name}
        logoUrl={use120x60Logo ? logoUrls._120x60 : logoUrls._88x31}
      />
    );
  };

  const generateMerchantGroupsForCarousel = (onlineMerchants) => {
    const PER_CHUNK = 12; // merchants per chunk

    const merchantGroups = onlineMerchants.reduce((merchantGroup, merchant, index) => {
      const chunkIndex = Math.floor(index / PER_CHUNK);

      if (!merchantGroup[chunkIndex]) {
        merchantGroup[chunkIndex] = [];
      }
      merchantGroup[chunkIndex].push(renderPotentialFavorite(merchant));

      return merchantGroup;
    }, []);

    return merchantGroups.map(chunk => <div key={chunk[0].key}>{chunk}</div>);
  };

  if (isEmpty(onlineMerchants)) {
    return (null);
  }

  return (
    <div className="mn_slide mn_slideFavorites">
      <div className="mn_content">
        <SlideCounter stepsData={stepsData} />
        <div className="mn_gap" />
        <h2>{favoritesHeading}</h2>
        <p>{favoritesText}</p>
        <div className="mn_pickFavorites">
          {shouldRenderCarousel ?
            <List shouldUseCarousel options={carousel}>
              { generateMerchantGroupsForCarousel(onlineMerchants) }
            </List>
            : onlineMerchants.map(merchant => renderPotentialFavorite(merchant))
          }
        </div>
        <div className="mn_bottomContent">
          <button
            className="mn_button mn_stepButton mn_stepFavoritesDone"
            onClick={() => handleFavoritesDoneClickWithAnalyticsAction(true)}
          >
            {favoritesCTAText}
          </button>
          {shouldShowLaterButton &&
            <button
              className="mn_basicButton mn_stepLaterLink mn_favoriteLaterLink"
              onClick={() => handleFavoritesDoneClickWithAnalyticsAction(false)}
            >
              I'll pick my favorites later<span className="mn_dot">.</span>
            </button>
          }
          <div className="mn_optInNotice">{optInNotice}</div>
        </div>
      </div>
    </div>
  );
}

export default Favorites;
